import { cn } from 'cn';

import { horarioDentroDaJanela } from '@shared/horario';

import { DIAS_SEMANA } from '../dias-semana';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import {
  opcoesDeHorario,
  type DiaValor,
  type DisponibilidadeProfessor,
  type ProgramacaoDia,
  type ProgramacaoSemanal,
} from './programacao-semanal';

export { programacaoSemanalVazia } from './programacao-semanal';
export type { ProgramacaoDia, ProgramacaoSemanal } from './programacao-semanal';

// Sem professor informado, cai numa janela ampla de horário comercial pra nunca deixar `valor.horario` fora das opções.
const JANELA_PADRAO = { horarioInicial: '07:00', horarioFinal: '21:00' };

/**
 * Controlada: quem chama decide o que `onChange` faz. `professor` restringe a grade à disponibilidade real dele
 * (dias desabilitados, `min`/`max` do horário), a mesma regra que `horarios.service.ts` valida.
 */
export function ProgramacaoSemanalGrid({
  valores,
  onChange,
  disabled = false,
  professor,
  preservarHorarioExistente = false,
}: {
  valores: ProgramacaoSemanal;
  onChange: (dia: DiaValor, valor: ProgramacaoDia) => void;
  disabled?: boolean;
  professor?: DisponibilidadeProfessor;
  preservarHorarioExistente?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Dia</th>
            <th className="px-3 py-2 text-left font-medium">Frequenta</th>
            <th className="px-3 py-2 text-left font-medium">Horário</th>
          </tr>
        </thead>
        <tbody>
          {DIAS_SEMANA.map((dia) => {
            const valor = valores[dia.valor];
            const diaDisponivel = !professor || professor.diasDisponiveis.includes(dia.valor);
            const opcoes = opcoesDeHorario(
              professor?.horarioInicial ?? JANELA_PADRAO.horarioInicial,
              professor?.horarioFinal ?? JANELA_PADRAO.horarioFinal,
            );
            const horarioAtualValido = horarioDentroDaJanela(
              valor.horario,
              professor?.horarioInicial ?? JANELA_PADRAO.horarioInicial,
              professor?.horarioFinal ?? JANELA_PADRAO.horarioFinal,
            );
            if (
              (preservarHorarioExistente || horarioAtualValido) &&
              !opcoes.includes(valor.horario)
            ) {
              opcoes.push(valor.horario);
              opcoes.sort();
            }
            return (
              <tr key={dia.valor} className={cn('border-t', !diaDisponivel && 'bg-muted/30')}>
                <td className="px-3 py-2">
                  {dia.label}
                  {!diaDisponivel ? (
                    <span className="ml-2 text-xs text-muted-foreground">professor não atende</span>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <Switch
                    disabled={disabled || !diaDisponivel}
                    checked={valor.frequenta}
                    onCheckedChange={(frequenta) =>
                      onChange(dia.valor, {
                        frequenta,
                        horario:
                          frequenta && !preservarHorarioExistente && !horarioAtualValido
                            ? (opcoes[0] ?? valor.horario)
                            : valor.horario,
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  <Select
                    value={valor.horario}
                    onValueChange={(horario) => onChange(dia.valor, { ...valor, horario })}
                    disabled={disabled || !diaDisponivel || !valor.frequenta}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {opcoes.map((horario) => (
                        <SelectItem key={horario} value={horario}>
                          {horario}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
